import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { ArrowLeft, Loader2, Pencil } from 'lucide-react';
import { BankFeeForm } from './BankFeeForm';

const TYPE_LABELS = {
  payment: 'Payment',
  payout: 'Payout',
  refund: 'Refund',
  chargeback: 'Chargeback'
};

const METHOD_LABELS = {
  etransfer_request_money: 'eTransfer Request Money',
  etransfer_standard: 'eTransfer Standard',
  etransfer_send_money: 'eTransfer Send Money'
};

const STATUS_LABELS = {
  approved: 'Approved',
  declined: 'Declined',
  returned: 'Returned'
};

import { Bank, BankFee } from '../../types';

const TYPE_METHOD_MAP = {
  payment: ['etransfer_request_money', 'etransfer_standard'],
  payout: ['etransfer_send_money'],
  refund: ['etransfer_send_money'],
  chargeback: ['etransfer_request_money']
};

export function BankDetails() {
  const { bankId } = useParams();
  const [bank, setBank] = useState<Bank | null>(null);
  const [fees, setFees] = useState<BankFee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFeeForm, setShowFeeForm] = useState(false);

  useEffect(() => {
    fetchBankAndFees();
  }, [bankId]);

  // Skip fetching for new bank form
  if (bankId === 'new') {
    return null;
  }

  async function fetchBankAndFees() {
    try {
      const [bankResult, feesResult] = await Promise.all([
        supabase.from('banks').select('*').eq('id', bankId).single(),
        supabase.from('bank_fees').select('*').eq('bank_id', bankId),
      ]);

      if (bankResult.error) throw bankResult.error;
      if (feesResult.error) throw feesResult.error;

      setBank(bankResult.data);
      setFees(feesResult.data || []);
    } catch (err) {
      console.error('Error fetching bank details:', err);
      setError('Failed to load bank details');
    } finally {
      setLoading(false);
    }
  }

  const getFeesByTypeAndMethod = (type: string, method: string) => {
    return fees.filter(fee => fee.type === type && fee.payment_method === method);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error || !bank) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{error || 'Bank not found'}</p>
        <Link to="/banks" className="text-indigo-600 hover:text-indigo-500 mt-4 inline-flex items-center">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Banks
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <Link
            to="/banks"
            className="flex items-center text-sm text-indigo-600 hover:text-indigo-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Providers
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">
            {bank.name} <span className="text-gray-500">({bank.alias})</span>
          </h1>
        </div>
        <button
          onClick={() => setShowFeeForm(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <Pencil className="w-4 h-4 mr-2" />
          Edit Provider Fees
        </button>
      </div>

      {Object.entries(TYPE_LABELS).map(([type, typeLabel]) => (
        <div key={type} className="bg-white shadow rounded-lg mb-8">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
            <h2 className="text-lg font-medium text-gray-900">{typeLabel}</h2>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
              {TYPE_METHOD_MAP[type as keyof typeof TYPE_METHOD_MAP].map((method) => (
                <React.Fragment key={method}>
                  <div className="col-span-3">
                    <h3 className="text-base font-medium text-gray-800 mb-4">
                      {METHOD_LABELS[method as keyof typeof METHOD_LABELS]}
                    </h3>
                  </div>
                  {getFeesByTypeAndMethod(type, method).map((fee) => (
                    <div
                      key={fee.status}
                      className="bg-gray-50 rounded-lg p-6 border border-gray-200"
                    >
                      <h4 className="text-sm font-medium text-gray-900 mb-4">
                        {STATUS_LABELS[fee.status]}
                      </h4>
                      <dl className="space-y-3">
                        <div>
                          <dt className="text-xs text-gray-500">Per Transaction</dt>
                          <dd className="text-sm font-medium text-gray-900">
                            ${fee.per_transaction_fee.toFixed(2)}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-500">Rate Fee</dt>
                          <dd className="text-sm font-medium text-gray-900">
                            {fee.rate_fee_percentage.toFixed(2)}%
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-500">Max Fee Cap</dt>
                          <dd className="text-sm font-medium text-gray-900">
                            ${fee.max_fee_cap.toFixed(2)}
                          </dd>
                        </div>
                      </dl>
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      ))}

      {showFeeForm && (
        <BankFeeForm
          bank={bank}
          onClose={() => {
            setShowFeeForm(false);
            fetchBankAndFees();
          }}
        />
      )}
    </div>
  );
}